import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const CTFContainer = styled.section`
  padding: 6rem 0;
  background: ${({ theme }) => theme.colors.background.primary};
  position: relative;
  overflow: hidden;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 3rem 0;
  }
`;

const AnimatedBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 0;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: 
      radial-gradient(circle at 20% 30%, rgba(155, 89, 182, 0.08) 0%, transparent 50%),
      radial-gradient(circle at 80% 70%, rgba(255, 229, 0, 0.05) 0%, transparent 50%),
      radial-gradient(circle at 50% 50%, rgba(0, 128, 214, 0.03) 0%, transparent 50%);
    animation: pulse 6s ease-in-out infinite;
  }
  
  @keyframes pulse {
    0%, 100% { opacity: 0.5; }
    50% { opacity: 1; }
  }
`;

const CTFHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;
  position: relative;
  z-index: 2;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    margin-bottom: 2rem;
    padding: 0 1rem;
  }
`;

const CTFMainTitle = styled(motion.h1)`
  font-size: 3.5rem;
  margin-bottom: 1rem;
  background: ${({ theme }) => theme.gradients.cyber};
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 2px;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 2.5rem;
    letter-spacing: 1px;
  }
`;

const CTFSubtitle = styled(motion.p)`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 1.2rem;
  max-width: 800px;
  margin: 0 auto 2rem;
  line-height: 1.6;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 1rem;
    padding: 0 1rem;
  }
`;

const SponsorBadge = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  background: ${({ theme }) => theme.colors.surface};
  padding: 2rem;
  border-radius: 20px;
  border: 2px solid ${({ theme }) => theme.colors.primary.cyan};
  margin-bottom: 2rem;
  box-shadow: 0 8px 25px rgba(155, 89, 182, 0.2);
  max-width: 400px;
  margin-left: auto;
  margin-right: auto;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 1.5rem;
    gap: 0.75rem;
    max-width: 300px;
  }
`;

const SponsorLogoContainer = styled.div`
  width: 200px;
  height: 60px;
  background: #FFFFFF;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.25rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    width: 160px;
    height: 48px;
    padding: 0.5rem;
  }
`;

const SponsorLogo = styled.img`
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  object-fit: contain;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    /* Logo will scale naturally within container */
  }
`;

const SponsorText = styled.div`
  text-align: center;
  
  .main-text {
    color: ${({ theme }) => theme.colors.primary.cyan};
    font-weight: 700;
    font-size: 1.1rem;
    margin-bottom: 0.25rem;
  }
  
  .sub-text {
    color: ${({ theme }) => theme.colors.text.secondary};
    font-size: 0.9rem;
    font-weight: 500;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    .main-text {
      font-size: 1rem;
    }
    
    .sub-text {
      font-size: 0.8rem;
    }
  }
`;

const CTFGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  z-index: 2;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    gap: 1.5rem;
    padding: 0 1rem;
  }
`;

const InfoCard = styled(motion.div)`
  background: ${({ theme }) => theme.gradients.card};
  border-radius: 20px;
  padding: 2.5rem;
  border: 1px solid ${({ theme }) => theme.colors.border};
  backdrop-filter: blur(20px);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 4px;
    background: ${({ theme }) => theme.gradients.primary};
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 1.5rem;
    border-radius: 15px;
  }
`;

const CardTitle = styled.h3`
  font-size: 1.8rem;
  color: ${({ theme }) => theme.colors.primary.yellow};
  margin-bottom: 1.5rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 0.8rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 1.5rem;
    margin-bottom: 1rem;
    gap: 0.6rem;
  }
`;

const EventDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 2rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    gap: 0.75rem;
    margin-bottom: 1.5rem;
  }
`;

const DetailItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem;
  background: rgba(0, 128, 214, 0.1);
  border-radius: 10px;
  border-left: 4px solid ${({ theme }) => theme.colors.primary.blue};
  
  .icon {
    font-size: 1.3rem;
    min-width: 24px;
  }
  
  .content {
    flex: 1;
    
    .label {
      color: ${({ theme }) => theme.colors.primary.blue};
      font-weight: 600;
      font-size: 0.9rem;
      margin-bottom: 0.2rem;
    }
    
    .value {
      color: ${({ theme }) => theme.colors.text.primary};
      font-size: 1rem;
      font-weight: 500;
    }
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 0.6rem;
    gap: 0.75rem;
    
    .icon {
      font-size: 1.1rem;
    }
    
    .content {
      .label {
        font-size: 0.8rem;
      }
      
      .value {
        font-size: 0.9rem;
      }
    }
  }
`;

const PrizesGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const PrizeCategory = styled.div`
  background: rgba(255, 229, 0, 0.1);
  border-radius: 15px;
  padding: 1.5rem;
  border: 1px solid ${({ theme }) => theme.colors.primary.yellow};
  
  h4 {
    color: ${({ theme }) => theme.colors.primary.yellow};
    font-size: 1.2rem;
    font-weight: 700;
    margin-bottom: 1rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 1rem;
    
    h4 {
      font-size: 1.1rem;
      margin-bottom: 0.75rem;
    }
  }
`;

const PrizeList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const PrizeItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  
  .place {
    font-size: 1.2rem;
    min-width: 24px;
  }
  
  .details {
    flex: 1;
    
    .position {
      color: ${({ theme }) => theme.colors.text.primary};
      font-weight: 600;
      font-size: 0.9rem;
    }
    
    .prize {
      color: ${({ theme }) => theme.colors.primary.yellow};
      font-size: 0.8rem;
      font-weight: 500;
    }
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    gap: 0.5rem;
    padding: 0.4rem;
    
    .place {
      font-size: 1rem;
    }
    
    .details {
      .position {
        font-size: 0.8rem;
      }
      
      .prize {
        font-size: 0.75rem;
      }
    }
  }
`;

const CTASection = styled(motion.div)`
  background: ${({ theme }) => theme.gradients.cyber};
  border-radius: 20px;
  padding: 2.5rem;
  text-align: center;
  position: relative;
  overflow: hidden;
  margin-top: 3rem;
  
  &::after {
    content: '';
    position: absolute;
    top: -50%;
    right: -50%;
    width: 200px;
    height: 200px;
    background: radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%);
    border-radius: 50%;
    animation: float 8s ease-in-out infinite;
  }
  
  @keyframes float {
    0%, 100% { transform: translate(0, 0) rotate(0deg); }
    33% { transform: translate(30px, -30px) rotate(120deg); }
    66% { transform: translate(-20px, 20px) rotate(240deg); }
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 2rem;
    margin-top: 2rem;
    border-radius: 15px;
  }
`;

const CTATitle = styled.h3`
  font-size: 2rem;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 1rem;
  font-weight: 800;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 1.5rem;
    margin-bottom: 0.75rem;
  }
`;

const CTADescription = styled.p`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 1.1rem;
  margin-bottom: 2rem;
  line-height: 1.6;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 1rem;
    margin-bottom: 1.5rem;
  }
`;

const RegisterButton = styled(motion.a)`
  display: inline-flex;
  align-items: center;
  gap: 0.8rem;
  background: ${({ theme }) => theme.colors.primary.yellow};
  color: ${({ theme }) => theme.colors.background.primary};
  padding: 1.2rem 2.5rem;
  border-radius: 30px;
  font-size: 1.1rem;
  font-weight: 700;
  text-decoration: none;
  text-transform: uppercase;
  letter-spacing: 1px;
  box-shadow: 0 8px 25px rgba(255, 229, 0, 0.4);
  transition: all 0.3s ease;
  position: relative;
  z-index: 10;
  
  &:hover {
    background: ${({ theme }) => theme.colors.primary.blue};
    color: ${({ theme }) => theme.colors.text.primary};
    transform: translateY(-3px);
    box-shadow: 0 12px 35px rgba(0, 128, 214, 0.4);
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 1rem 2rem;
    font-size: 1rem;
    gap: 0.6rem;
  }
`;

const CTFComponent = () => {
  const [logoError, setLogoError] = React.useState(false);

  const handleLogoError = () => {
    setLogoError(true);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <CTFContainer id="ctf">
      <AnimatedBackground />
      <div className="container">
        <CTFHeader>
          <CTFMainTitle
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            🔓 Capture The Flag
          </CTFMainTitle>
          
          <CTFSubtitle
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
          >
            Get ready to hack, solve, and conquer! Test your cybersecurity skills against 
            some of the brightest minds in the field. Whether you're joining us onsite or 
            competing online, this is your chance to shine.
          </CTFSubtitle>
          
          <SponsorBadge
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            viewport={{ once: true }}
          >
            <SponsorLogoContainer>
              {!logoError ? (
                <SponsorLogo
                  src="/sponsors/hackerone.png"
                  alt="HackerOne"
                  onError={handleLogoError}
                />
              ) : (
                <span style={{ fontSize: '2rem', color: '#333' }}>🐛 HackerOne</span>
              )}
            </SponsorLogoContainer>
            <SponsorText>
              <div className="main-text">Proudly Sponsored by</div>
              <div className="sub-text">HackerOne - The #1 Hacker-Powered Security Platform</div>
            </SponsorText>
          </SponsorBadge>
        </CTFHeader>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <CTFGrid>
            <InfoCard variants={itemVariants}>
              <CardTitle>📅 Event Details</CardTitle>
              <EventDetails>
                <DetailItem>
                  <div className="icon">📅</div>
                  <div className="content">
                    <div className="label">Date</div>
                    <div className="value">July 25–26, 2025</div>
                  </div>
                </DetailItem>
                <DetailItem>
                  <div className="icon">⏰</div>
                  <div className="content">
                    <div className="label">Time</div>
                    <div className="value">9:00 AM – 4:00 PM MT</div>
                  </div>
                </DetailItem>
                <DetailItem>
                  <div className="icon">📍</div>
                  <div className="content">
                    <div className="label">Location</div>
                    <div className="value">Online & Onsite</div>
                  </div>
                </DetailItem>
                <DetailItem>
                  <div className="icon">🎯</div>
                  <div className="content">
                    <div className="label">Format</div>
                    <div className="value">Individual Competition</div>
                  </div>
                </DetailItem>
              </EventDetails>
            </InfoCard>

            <InfoCard variants={itemVariants}>
              <CardTitle>🏆 Prizes & Rewards</CardTitle>
              <PrizesGrid>
                <PrizeCategory>
                  <h4>💻 Online Winners</h4>
                  <PrizeList>
                    <PrizeItem>
                      <div className="place">🥇</div>
                      <div className="details">
                        <div className="position">Winner</div>
                        <div className="prize">$1000</div>
                      </div>
                    </PrizeItem>
                    <PrizeItem>
                      <div className="place">🥈</div>
                      <div className="details">
                        <div className="position">Runner Up</div>
                        <div className="prize">6-Month HTB Subscription</div>
                      </div>
                    </PrizeItem>
                    <PrizeItem>
                      <div className="place">🥉</div>
                      <div className="details">
                        <div className="position">2nd Runner Up</div>
                        <div className="prize">3-Month HTB Subscription</div>
                      </div>
                    </PrizeItem>
                    <PrizeItem>
                      <div className="place">🏅</div>
                      <div className="details">
                        <div className="position">4th–10th Place</div>
                        <div className="prize">1-Month HTB Subscription</div>
                      </div>
                    </PrizeItem>
                  </PrizeList>
                </PrizeCategory>

                <PrizeCategory>
                  <h4>🏢 Onsite Winners</h4>
                  <PrizeList>
                    <PrizeItem>
                      <div className="place">🥇</div>
                      <div className="details">
                        <div className="position">Winner</div>
                        <div className="prize">$1000 + BSides ABQ Coin</div>
                      </div>
                    </PrizeItem>
                    <PrizeItem>
                      <div className="place">🥈</div>
                      <div className="details">
                        <div className="position">Runner Up</div>
                        <div className="prize">$500 + BSides ABQ Coin</div>
                      </div>
                    </PrizeItem>
                    <PrizeItem>
                      <div className="place">🥉</div>
                      <div className="details">
                        <div className="position">2nd Runner Up</div>
                        <div className="prize">$350 + BSides ABQ Coin</div>
                      </div>
                    </PrizeItem>
                  </PrizeList>
                </PrizeCategory>
              </PrizesGrid>
            </InfoCard>
          </CTFGrid>

          <CTASection variants={itemVariants}>
            <CTATitle>🧠 Think You're Up for the Challenge?</CTATitle>
            <CTADescription>
              Pre-register now and secure your spot in the most exciting CTF competition 
              at Security BSides Albuquerque 2025. Start sharpening your skills!
            </CTADescription>
            <RegisterButton
              href="https://bsidesabq.ctfd.io/"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              🚀 Pre-Register Now
            </RegisterButton>
          </CTASection>
        </motion.div>
      </div>
    </CTFContainer>
  );
};

export default CTFComponent;